import React, { createContext, useEffect, useState } from 'react'
import useConfigApi from '../hooks/useConfigApi'
import { IFeatureFlag } from '../types/config-api-types'

// Extend window type for feature flags accessible outside React
declare global {
    interface Window {
        __FEATURE_FLAGS__?: Record<string, boolean>
    }
}

const GlobalFeatureFlagContext = createContext<IFeatureFlag[]>([])
const FeatureFlagLoadingContext = createContext<boolean>(true)

const GlobalFeatureFlagProvider = ({
    children,
}: {
    children: React.ReactNode
}) => {
    const { getGlobalFeatureFlagsAsync } = useConfigApi()
    const [globalFeatureFlags, setGlobalFeatureFlags] = useState<
        IFeatureFlag[]
    >([])
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        const TIMEOUT_MS = 10000

        const loadWithTimeout = async () => {
            try {
                const timeoutPromise = new Promise((_, reject) => {
                    setTimeout(() => reject(new Error('Feature flags request timed out')), TIMEOUT_MS)
                })

                // Race between API call and timeout
                const result = await Promise.race([
                    getGlobalFeatureFlagsAsync(),
                    timeoutPromise
                ]) as any

                if (result.status === 'Success' && result.data) {
                    setGlobalFeatureFlags(result.data)
                    // Store flags in window for non-React access (e.g., axios interceptors)
                    window.__FEATURE_FLAGS__ = result.data.reduce((acc: Record<string, boolean>, flag: IFeatureFlag) => {
                        acc[flag.name] = flag.enabled
                        return acc
                    }, {})
                } else {
                    console.warn('Feature flags loaded but status was not Success, defaulting to empty')
                    setGlobalFeatureFlags([])
                    window.__FEATURE_FLAGS__ = {}
                }
            } catch (error) {
                console.error('Error loading global feature flags:', error)
                // Ensure flags default to empty array (all flags will be false)
                setGlobalFeatureFlags([])
                window.__FEATURE_FLAGS__ = {}
            } finally {
                setIsLoading(false)
            }
        }

        loadWithTimeout()
    }, []) // Only run once on mount

    return (
        <FeatureFlagLoadingContext.Provider value={isLoading}>
            <GlobalFeatureFlagContext.Provider value={globalFeatureFlags}>
                {children}
            </GlobalFeatureFlagContext.Provider>
        </FeatureFlagLoadingContext.Provider>
    )
}
export const IsFeatureFlagEnabled = (flagName: string) => {
    const globalFeatureFlags = React.useContext(GlobalFeatureFlagContext)

    return (
        globalFeatureFlags.find(flag => flag.name === flagName)
            ?.enabled || false
    )
}

export const useFeatureFlagValue = (flagName: string): boolean => {
    const globalFeatureFlags = React.useContext(GlobalFeatureFlagContext)
    return globalFeatureFlags.find(flag => flag.name === flagName)?.enabled || false
}

export const useFeatureFlag = (flagName: string) => {
    const globalFeatureFlags = React.useContext(GlobalFeatureFlagContext)
    const isLoading = React.useContext(FeatureFlagLoadingContext)

    const isEnabled = globalFeatureFlags.find(flag => flag.name === flagName)
        ?.enabled || false

    return {
        isEnabled,
        isLoading
    }
}

export const useFeatureFlagLoading = () => {
    return React.useContext(FeatureFlagLoadingContext)
}

export default GlobalFeatureFlagProvider
